import cn from 'classnames';
import { Drawer, Form, Layout, Core, Localization } from 'connex-cds';
import { find, isEmpty, omit } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { useCompanySetup, useReasonCodeSetup, useVehicleSetup, useVehicleTypeSetup } from './MasterDataProvider';
import { useTicketContext } from './TicketContext';
import { cleanupLineItems } from '../../../../util/cleanupLineItems';

const Styled = styled.div`
  padding-top: 24px;
  .reason {
    border: var(--color-border-container);
    background-color: var(--color-background-container);
    margin-bottom: 10px;
    &.active {
      background-color: #d9d9d9;
    }
  }
`;

export const LoadRejection = () => {
  const { translateObjects } = Localization.useTranslateObjects();
  const {
    Components: { SubmitButton },
    values,
    setFieldValue,
    errors,
  } = Form.useFormContext();

  const { closeDrawer } = Drawer.useDrawerContext();

  const { ticket } = useTicketContext();

  const vehicleSetup = useVehicleSetup();
  const reasonCodeSetup = useReasonCodeSetup();
  const companySetup = useCompanySetup();
  const vehicleTypeSetup = useVehicleTypeSetup();

  const vehicleType = React.useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    const targetVehicleType = find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
    return targetVehicleType?.id;
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const activeReasons = React.useMemo(
    () =>
      reasonCodeSetup?.data
        ?.filter?.(d => d.type === 'REJECT')
        ?.filter?.(reason => reason.status === 'ACTIVE')
        ?.filter?.(reason =>
          companySetup?.data?.isMulticountry === true
            ? reason?.countries?.includes(ticket?.origin?.address?.countryCode) || !reason?.countries?.length
            : reason
        )
        ?.filter?.(reason => reason?.vehicleTypes?.includes(vehicleType) || !reason?.vehicleTypes?.length) || [],
    [companySetup?.data?.isMulticountry, reasonCodeSetup?.data, ticket?.origin?.address?.countryCode, vehicleType]
  );

  const options = React.useMemo(() => {
    if (!activeReasons?.length) {
      return [];
    }

    const reasonCodes = translateObjects(activeReasons, {
      getStringId: x => `SETUP_REASON-CODE_${x}`.replace(/\s/gi, '_').toUpperCase(),
      getPath: 'id',
      setPath: 'label',
      defaultMessagePath: 'description',
    });

    if (!reasonCodes) {
      return activeReasons;
    }

    return reasonCodes.map(reason => ({
      id: reason?.crn,
      label: reason?.label ?? reason?.description ?? reason?.name,
      isDefault: reason?.isDefault,
    }));
  }, [activeReasons]);

  // using default option as a selected option
  const defaultOption = React.useMemo(() => {
    return options.filter(option => option.isDefault);
  }, [options]);

  const disabled = React.useMemo(() => {
    if (companySetup?.data?.isReturnedMaterialMandatory) {
      const errorsToCheck = cleanupLineItems(errors, values, 'ticketEvents', 'returnConcreteOnTruck');
      return !isEmpty(errorsToCheck) || !values?.customerRejectReason;
    }

    const errorsToCheck = cleanupLineItems(errors, values, 'ticketEvents');
    return !isEmpty(errorsToCheck) || !values?.customerRejectReason;
  }, [companySetup?.data?.isReturnedMaterialMandatory, errors, values]);

  React.useEffect(() => {
    if (values?.customerRejectReason) {
      setFieldValue('customerStatus', 'REJECTED');
      setFieldValue('finalized', true);
    }
  }, [setFieldValue, values?.customerRejectReason]);

  React.useEffect(() => {
    if (defaultOption.length >= 1) {
      setFieldValue('customerRejectReason', find(reasonCodeSetup.data, { crn: defaultOption[0].id }));
    }
  }, []);

  const handleSave = React.useCallback(
    reason => {
      const targetReason = find(reasonCodeSetup.data, { crn: reason });
      setFieldValue('customerRejectReason', targetReason);
      setFieldValue('signature', undefined);
    },
    [reasonCodeSetup.data, setFieldValue]
  );

  return (
    <Styled className={cn('load-rejection')}>
      <Core.Spinner spin={vehicleSetup.isLoading || reasonCodeSetup.isLoading}>
        {options.map(option => {
          return (
            <div
              className={cn('reason', { active: values?.customerRejectReason?.crn === option.id })}
              style={{
                padding: '3px 10px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
              onClick={() => {
                handleSave(option.id);
              }}
            >
              {option.label}
            </div>
          );
        })}
        <SubmitButton
          stringId="submitRejection"
          data-testid="submit-driver-rejection-button"
          onClick={closeDrawer}
          disabled={disabled}
        />
      </Core.Spinner>
    </Styled>
  );
};
